import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, PenTool, Clock, Car, Check, Star, Download } from 'lucide-react';
// Placeholder for MechanicWorkshopScene
// import MechanicWorkshopScene from '../components/MiniMechanicsShop';

const MechanicsServicePage = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.3 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div className="min-h-screen bg-zonex-white text-zonex-gray-900">
            {/* Header Hero Section */}
            <section className="py-28 bg-gradient-to-br from-zonex-white to-zonex-lightGray relative overflow-hidden">
                {/* Red accent gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-zonex-red/10 via-transparent to-transparent z-0"></div>

                {/* Accent shapes */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/15 rounded-full blur-3xl animate-float-slow"></div>
                <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow-reverse"></div>

                {/* Content */}
                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">
                            Professional Automotive Services <span className="text-zonex-red">Delivered</span>
                        </h1>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-lg text-zonex-gray-700 mb-8">
                            ZoneX connects vehicle owners with certified mechanics for on-site service and repair. Our vetted network of professionals brings expertise directly to your location—whether at home, work, or on the road.
                        </p>
                        <Link to="/">
                            <button className="bg-zonex-red text-white px-8 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center mx-auto shadow-md hover:shadow-lg">
                                Back to Home <ChevronRight className="w-5 h-5 ml-2" />
                            </button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* 3D Animation Section */}
            <section className="py-16 bg-zonex-lightGray relative overflow-hidden">
                <div className="container mx-auto px-4">
                    <div className="w-full h-96 rounded-xl overflow-hidden border border-zonex-red/30 shadow-red-lg">
                        {/* Placeholder for MechanicWorkshopScene */}
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-zonex-red/20 to-zonex-gray-200">
                            <img
                                src="/"
                                alt="Certified mechanic working on vehicle through ZoneX on-demand mechanic service"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section ref={sectionRef} className="py-20 bg-zonex-white relative overflow-hidden">
                {/* Accent elements */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow"></div>
                <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-zonex-red/5 rounded-full blur-3xl animate-float-slow-reverse"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Key Service Benefits</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Experience premium vehicle maintenance with our network of skilled automotive professionals.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <PenTool className="w-8 h-8 text-zonex-red" />,
                                title: "Rapid Emergency Response",
                                description: "Get immediate assistance for breakdowns and roadside emergencies with technicians arriving within 30-60 minutes."
                            },
                            {
                                icon: <Clock className="w-8 h-8 text-zonex-red" />,
                                title: "Convenient Scheduling",
                                description: "Book regular maintenance at your preferred time and location, eliminating the need to visit service centers."
                            },
                            {
                                icon: <Check className="w-8 h-8 text-zonex-red" />,
                                title: "Certified Technicians",
                                description: "Every mechanic in our network is verified, background-checked, and fully certified with proven expertise."
                            },
                            {
                                icon: <Car className="w-8 h-8 text-zonex-red" />,
                                title: "Comprehensive Vehicle Support",
                                description: "Service for cars, motorcycles, and commercial vehicles of all makes and models with specialized expertise."
                            },
                            {
                                icon: <Star className="w-8 h-8 text-zonex-red" />,
                                title: "Transparent Pricing",
                                description: "Upfront cost estimates with detailed breakdowns and no hidden fees, approved by you before work begins."
                            },
                            {
                                icon: <PenTool className="w-8 h-8 text-zonex-red" />,
                                title: "At-Your-Location Service",
                                description: "Mechanics come to your home, office, or anywhere your vehicle is located with all necessary equipment."
                            }
                        ].map((feature, index) => (
                            <div
                                key={index}
                                className={`bg-zonex-white p-6 rounded-xl border border-gray-200 hover:border-zonex-red/50 shadow-md hover:shadow-lg hover:-translate-y-2 transition-all duration-500 group ${
                                    isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                                } transition-opacity duration-1000`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="mb-4 p-3 bg-zonex-red/10 rounded-lg group-hover:bg-zonex-red/20 transition-colors">{feature.icon}</div>
                                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                                <p className="text-zonex-gray-700 mb-6">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-20 bg-zonex-lightGray relative">
                {/* Enhanced accent elements */}
                <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-r from-zonex-red/20 to-zonex-lightGray opacity-30 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 right-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Seamless Service Process</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Getting expert mechanical assistance through ZoneX is simple and efficient. Here's how it works:
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                number: "01",
                                title: "Describe Your Need",
                                description: "Detail your vehicle issue with our guided diagnostic tool, adding photos for better assessment.",
                                icon: <PenTool className="w-8 h-8 text-zonex-red" />
                            },
                            {
                                number: "02",
                                title: "Select Expert Match",
                                description: "Our system connects you with specialized mechanics based on your vehicle and issue type.",
                                icon: <Check className="w-8 h-8 text-zonex-red" />
                            },
                            {
                                number: "03",
                                title: "Confirm Service Details",
                                description: "Review transparent pricing estimate and approve the work before technician arrival.",
                                icon: <Clock className="w-8 h-8 text-zonex-red" />
                            },
                            {
                                number: "04",
                                title: "Receive Quality Service",
                                description: "Mechanic arrives, performs the work, and provides digital documentation and warranty.",
                                icon: <Car className="w-8 h-8 text-zonex-red" />
                            }
                        ].map((step, index) => (
                            <div
                                key={index}
                                className={`relative group ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'} transition-all duration-1000`}
                                style={{ transitionDelay: `${index * 200}ms` }}
                            >
                                <div className="bg-zonex-white p-8 rounded-xl h-full flex flex-col hover:bg-gradient-to-b hover:from-zonex-white hover:to-zonex-lightGray transition-all duration-300 border border-gray-200 hover:border-zonex-red/50 shadow-md hover:shadow-lg">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="p-3 bg-zonex-red/10 rounded-lg group-hover:bg-zonex-red/20 transition-colors">
                                            {step.icon}
                                        </div>
                                        <span className="text-4xl font-bold text-zonex-red/20">{step.number}</span>
                                    </div>
                                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                                    <p className="text-zonex-gray-700">{step.description}</p>

                                    {/* Enhanced connector line with red gradient */}
                                    {index < 3 && (
                                        <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-1 bg-gradient-to-r from-zonex-red/80 to-zonex-red/10"></div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service Offerings */}
            <section className="py-20 bg-zonex-white relative">
                <div className="absolute top-0 right-1/4 w-1/5 h-1/5 bg-zonex-red/15 rounded-full blur-3xl animate-float-slow"></div>
                <div className="absolute bottom-0 left-1/3 w-1/4 h-1/4 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow-reverse"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Professional Service Offerings</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Our certified mechanics provide a comprehensive range of services to keep your vehicle running at peak performance.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                title: "Preventive Maintenance",
                                description: "Regular servicing including oil changes, filter replacements, fluid checks, and multi-point inspections to prevent future issues.",
                                price: "From ₹999"
                            },
                            {
                                title: "Advanced Diagnostics",
                                description: "Comprehensive engine and system diagnostics using professional-grade tools to pinpoint issues with precision and accuracy.",
                                price: "From ₹1,499"
                            },
                            {
                                title: "Complete Brake Service",
                                description: "Brake pad replacements, rotor resurfacing, caliper maintenance, and full brake system inspection for optimal safety.",
                                price: "From ₹1,299"
                            },
                            {
                                title: "Electrical System Repair",
                                description: "Diagnose and repair electrical issues, battery replacements, alternator testing, and comprehensive wiring maintenance.",
                                price: "From ₹899"
                            },
                            {
                                title: "Climate Control Service",
                                description: "Air conditioning diagnostics, refrigerant recharging, compressor testing, and heating system maintenance for year-round comfort.",
                                price: "From ₹1,999"
                            },
                            {
                                title: "24/7 Emergency Response",
                                description: "Round-the-clock support for breakdowns, flat tires, battery jumps, and immediate roadside mechanical assistance.",
                                price: "From ₹799"
                            }
                        ].map((service, index) => (
                            <div
                                key={index}
                                className={`p-1 bg-gradient-to-r from-zonex-red/10 to-transparent rounded-xl hover:from-zonex-red/20 transition-all duration-500 ${
                                    isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                                } transition-opacity duration-1000`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="bg-zonex-white p-6 rounded-lg h-full flex flex-col border border-gray-200">
                                    <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                                    <p className="text-zonex-gray-700 flex-grow mb-4">{service.description}</p>
                                    <div className="flex justify-between items-center">
                                        {/* <span className="text-zonex-red font-bold">{service.price}</span> */}
                                        <Link to="/contact">
                                            <button className="bg-zonex-red/10 hover:bg-zonex-red/20 text-zonex-red px-4 py-2 rounded-full transition-all duration-300">
                                                Book Now
                                            </button>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-20 bg-zonex-lightGray relative">
                {/* Red accent shapes */}
                <div className="absolute top-0 right-1/4 w-1/5 h-1/5 bg-zonex-red/15 rounded-full blur-3xl animate-float-slow"></div>
                <div className="absolute bottom-0 left-1/3 w-1/4 h-1/4 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow-reverse"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Customer Success Stories</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Hear from vehicle owners who have experienced our mechanic services firsthand.
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {[
                                {
                                    text: "The ZoneX mechanic arrived within 30 minutes of my emergency call. He diagnosed the issue quickly and got my car running again without needing a tow. Saved me hours of stress and money!",
                                    author: "David Wilson",
                                    role: "Car Owner",
                                    rating: 5
                                },
                                {
                                    text: "As a busy professional, I love that I can schedule maintenance for the weekend, and the mechanic comes to my home. The pricing is transparent and the work quality is exceptional.",
                                    author: "Anita Desai",
                                    role: "Busy Professional",
                                    rating: 5
                                },
                                {
                                    text: "The diagnostic report was thorough and educational. The mechanic took time to explain what was happening with my car and provided options for addressing the issues. Great customer service!",
                                    author: "Carlos Rodriguez",
                                    role: "New Car Owner",
                                    rating: 4
                                },
                                {
                                    text: "Having a certified mechanic come to my office parking lot saved me an entire workday. The technician was professional, efficient, and completed the repair faster than I expected.",
                                    author: "Jennifer Lee",
                                    role: "Business Executive",
                                    rating: 5
                                }
                            ].map((testimonial, index) => (
                                <div
                                    key={index}
                                    className={`bg-zonex-white p-6 rounded-xl shadow-md border border-zonex-red/30 ${
                                        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                                    } transition-all duration-1000`}
                                    style={{ transitionDelay: `${index * 200}ms` }}
                                >
                                    <div className="flex mb-4">
                                        {[...Array(5)].map((_, i) => (
                                            <Star
                                                key={i}
                                                className={`w-5 h-5 ${i < testimonial.rating ? 'text-zonex-red fill-zonex-red' : 'text-gray-300'}`}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-lg text-zonex-gray-700 mb-6 italic">"{testimonial.text}"</p>
                                    <div className="flex items-center">
                                        <div className="w-12 h-12 bg-zonex-red/20 rounded-full mr-4 flex items-center justify-center">
                                            <span className="text-zonex-red font-bold">{testimonial.author.charAt(0)}</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">{testimonial.author}</h4>
                                            <p className="text-sm text-zonex-red/80">{testimonial.role}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section - Added for SEO */}
            <section className="py-20 bg-zonex-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Find answers to common questions about ZoneX's mechanic services
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto">
                        {[
                            {
                                question: "How quickly can a mechanic reach me in an emergency?",
                                answer: "In emergency situations, our mechanics typically reach your location within 30-60 minutes, depending on your location and current demand. You'll receive real-time updates and an accurate ETA in the app."
                            },
                            {
                                question: "Are ZoneX mechanics certified and insured?",
                                answer: "Yes, all mechanics in our network are certified professionals with relevant qualifications and expertise. Each mechanic undergoes thorough background checks, verification of certifications, and maintains appropriate insurance coverage."
                            },
                            {
                                question: "What types of vehicles do your mechanics service?",
                                answer: "Our mechanics service a wide range of vehicles including passenger cars, SUVs, motorcycles, and light commercial vehicles across most makes and models. When booking, you'll be matched with mechanics specifically experienced with your vehicle type."
                            },
                            {
                                question: "How does payment work for mechanic services?",
                                answer: "You'll receive a cost estimate before confirming your booking. After service completion and your approval, payment is processed securely through the ZoneX app using your preferred payment method. You'll receive a detailed digital invoice for your records."
                            },
                            {
                                question: "Do ZoneX mechanics provide warranties on their work?",
                                answer: "Yes, all services performed by ZoneX mechanics come with a standard 30-day warranty covering both parts and labor. For some specialized repairs, extended warranty options may be available."
                            }
                        ].map((faq, index) => (
                            <div key={index} className="mb-6 bg-zonex-lightGray rounded-xl overflow-hidden shadow-md">
                                <div className="px-6 py-4 border-l-4 border-zonex-red">
                                    <h3 className="text-lg font-semibold text-zonex-gray-900">{faq.question}</h3>
                                </div>
                                <div className="px-6 py-4 bg-white">
                                    <p className="text-zonex-gray-700">{faq.answer}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-zonex-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-zonex-red/10 to-transparent opacity-50"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="bg-gradient-to-br from-zonex-white to-zonex-lightGray rounded-2xl p-8 md:p-12 shadow-2xl border border-zonex-red/30">
                        <div className="md:flex items-center justify-between">
                            <div className="mb-8 md:mb-0 md:max-w-lg">
                                <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready for Expert Vehicle Service?</h2>
                                <div className="w-16 h-1 bg-zonex-red mb-6 rounded-full shadow-red-glow"></div>
                                <p className="text-zonex-gray-700">
                                    Download the ZoneX app today to connect with certified mechanics who come to you, providing convenience, quality, and peace of mind.
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/contact">
                                    <button className="bg-zonex-red text-white px-8 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg">
                                        Book a Mechanic <ChevronRight className="w-4 h-4 ml-1" />
                                    </button>
                                </Link>
                                <Link to="/contact">
                                    <button className="border border-zonex-red/50 text-zonex-red px-8 py-3 rounded-full hover:bg-zonex-red/10 transition-all duration-300 flex items-center justify-center">
                                        Contact Support
                                    </button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Custom CSS */}
            <style jsx>{`
                @keyframes float-slow {
                    0% { transform: translateY(0); }
                    50% { transform: translateY(-20px); }
                    100% { transform: translateY(0); }
                }
                
                @keyframes float-slow-reverse {
                    0% { transform: translateY(0); }
                    50% { transform: translateY(20px); }
                    100% { transform: translateY(0); }
                }
                
                .animate-float-slow {
                    animation: float-slow 8s ease-in-out infinite;
                }
                
                .animate-float-slow-reverse {
                    animation: float-slow-reverse 12s ease-in-out infinite;
                }
                
                .shadow-red-glow {
                    box-shadow: 0 0 15px rgba(252, 63, 63, 0.4);
                }
                
                .shadow-red-lg {
                    box-shadow: 0 10px 15px -3px rgba(252, 63, 63, 0.1), 0 4px 6px -2px rgba(252, 63, 63, 0.05);
                }
            `}</style>
        </div>
    );
};

export default MechanicsServicePage;